import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSupabaseQuery } from '../hooks/useSupabase';
import {
  Brain,
  FileText,
  History,
  TrendingUp,
  Calendar,
  ArrowRight,
  CheckCircle,
  ChevronRight,
  BarChart3,
  Clock,
} from 'lucide-react';

interface Assessment {
  id: string;
  user_id: string;
  status: 'in_progress' | 'completed' | 'archived';
  created_at: string;
  completed_at?: string;
}

interface Stats {
  totalAssessments: number;
  completedAssessments: number;
  averageCompletionTime: string;
}

export function Dashboard() {
  const { user, profile, loading } = useAuth();

  const { data: assessmentsData, loading: assessmentsLoading } = useSupabaseQuery<Assessment[]>(
    'assessments',
    {
      filter: (q: any) => q.eq('user_id', profile?.id),
      order: { column: 'created_at', ascending: false },
      enabled: !!profile?.id,
    }
  );

  const assessments = assessmentsData?.slice(0, 5) || [];

  const calculateAverageTime = (completedAssessments: Assessment[]) => {
    if (completedAssessments.length === 0) return '0 min';
    const totalMinutes = completedAssessments.reduce((acc, a) => {
      if (a.completed_at && a.created_at) {
        return acc + (new Date(a.completed_at).getTime() - new Date(a.created_at).getTime()) / (1000 * 60);
      }
      return acc;
    }, 0);
    const avg = Math.round(totalMinutes / completedAssessments.length);
    return `${avg} min`;
  };

  const stats: Stats | null = assessmentsData && assessmentsData.length > 0
    ? {
        totalAssessments: assessmentsData.length,
        completedAssessments: assessmentsData.filter(a => a.status === 'completed').length,
        averageCompletionTime: calculateAverageTime(
          assessmentsData.filter(a => a.status === 'completed')
        ),
      }
    : null;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30';
      case 'in_progress': return 'text-yellow-600 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30';
      case 'archived': return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Fullført';
      case 'in_progress': return 'Pågående';
      case 'archived': return 'Arkivert';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {user ? `Velkommen tilbake, ${profile?.fullName || user.email}` : 'Helsenøkkelen'}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Din personlige veileder for mental helse og velvære
          </p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <Link
            to="/assessment/start"
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700 p-6 hover:shadow-xl transition-shadow group"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg inline-block mb-4">
                  <Brain className="h-8 w-8 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Ny vurdering
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Start en ny helseanalyse og få personlige anbefalinger
                </p>
                <span className="text-green-600 dark:text-green-400 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  Start nå
                  <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </div>
          </Link>

          <Link
            to="/history"
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700 p-6 hover:shadow-xl transition-shadow group"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg inline-block mb-4">
                  <History className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Min historikk
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Se tidligere vurderinger og følg din utvikling over tid
                </p>
                <span className="text-blue-600 dark:text-blue-400 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  Se historikk
                  <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </div>
          </Link>

          <Link
            to="/resources"
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700 p-6 hover:shadow-xl transition-shadow group"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg inline-block mb-4">
                  <FileText className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  Ressurser
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Artikler, øvelser og verktøy for bedre mental helse
                </p>
                <span className="text-purple-600 dark:text-purple-400 font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                  Utforsk
                  <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </div>
          </Link>
        </div>

        {user && (
          <>
            {stats && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700 p-6 mb-8">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Din oversikt</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <BarChart3 className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Totalt vurderinger</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalAssessments}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <CheckCircle className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Fullførte</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.completedAssessments}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <Clock className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Gjennomsnitt tid</span>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.averageCompletionTime}</p>
                  </div>
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <TrendingUp className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">Fremgang</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                        <div
                          className="bg-green-600 dark:bg-green-500 h-2 rounded-full"
                          style={{ width: `${(stats.completedAssessments / stats.totalAssessments) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {Math.round((stats.completedAssessments / stats.totalAssessments) * 100)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {assessments.length > 0 && (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border dark:border-gray-700 p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Siste vurderinger</h2>
                  <Link to="/history" className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-500 font-medium">
                    Se alle
                  </Link>
                </div>
                <div className="space-y-4">
                  {assessments.map((assessment) => (
                    <div key={assessment.id} className="border dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {new Date(assessment.created_at).toLocaleDateString('nb-NO', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {new Date(assessment.created_at).toLocaleTimeString('nb-NO', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(assessment.status)}`}>
                            {getStatusText(assessment.status)}
                          </span>
                          {assessment.status === 'completed' && (
                            <Link
                              to={`/assessment/${assessment.id}/results`}
                              className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-500"
                            >
                              <ChevronRight className="h-5 w-5" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {!user && (
          <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-xl shadow-lg p-8 text-white">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-2xl font-bold mb-4">
                Få mest mulig ut av Helsenøkkelen
              </h2>
              <p className="text-lg mb-6 text-green-50">
                Opprett en gratis konto for å lagre dine vurderinger, følge din utvikling over tid,
                og få tilgang til personlige anbefalinger.
              </p>
              <div className="flex gap-4 justify-center">
                <Link
                  to="/signup"
                  className="bg-white text-green-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
                >
                  Opprett konto
                </Link>
                <Link
                  to="/login"
                  className="border-2 border-white text-white px-6 py-3 rounded-lg font-semibold hover:bg-white hover:text-green-600 transition-colors"
                >
                  Logg inn
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
